/**
 * Мова умов (docs/architecture/filter-language.md; FLT-20): дерево груп `and` / `or` і умов
 * `{ field, op, value?, excludeEmpty? }`. Значення — літерали або токени-об'єкти (`{ token: 'me' }`),
 * які сервер обчислює з контексту доступу під час запиту. Модуль без zod: його беруть DAL
 * (компілятор у SQL), браузер (оцінювач у чанку картки, AG-61), пакет компанії й автоматизації.
 *
 * Профіль — дозволені вузли, оператори, токени й межі місця застосування (розд. 5). Тут — профілі
 * `filter` (фільтри, подання, `when` автоматизацій; з токеном `record` — фільтр вибору зв'язку) і
 * `requirement` (умови над записом, що пишеться: умови входу в етап PIP-10, `requiredWhen` FLD-30,
 * правила між полями FLD-38; field-rules.md, розд. 2): він додає вузли FLT-16 — крок через зв'язок
 * і `related … exists` — і токен `record`. Профіль `rule` (правила видимості) зберігає пласкі
 * списки умов у документі ролі: його схема й allowlist операторів — у `@clientline/contracts/access`
 * (чутливий шлях, рев'ю Security), спільні тут лише токени.
 */

/**
 * Усі оператори контракту (розд. 2): відомі мові умов. Оператор, якого ще немає в профілі,
 * дає `filter_operator_not_allowed` (422), а не `request_malformed`: дерево з ним — коректне.
 */
export const CONTRACT_OPERATORS = [
  'eq',
  'neq',
  'in',
  'not_in',
  'is_null',
  'not_null',
  'lt',
  'lte',
  'gt',
  'gte',
  'between',
  'on',
  'before',
  'after',
  'within',
  'starts_with',
  'contains',
  'not_contains',
  'contains_digits',
  'domain',
  'has_any',
  'has_all',
  'has_none',
  'currency_in',
] as const;
export type ContractOperator = (typeof CONTRACT_OPERATORS)[number];

/**
 * Оператори профілю `filter`, які реалізує компілятор DAL (allowlist загального API). Який із них
 * діє на полі, визначає ще й тип поля (`FIELD_TYPE_REGISTRY[тип].operators`, FLT-12). Решта
 * операторів контракту (`contains`, `not_contains`, `contains_digits`, `domain`) — з індексами S3.
 */
export const FILTER_OPERATORS = [
  'eq',
  // «Не дорівнює» і «не є жодним із» включають порожні; `excludeEmpty: true` — без них (FLT-15).
  'neq',
  'in',
  'not_in',
  'is_null',
  'not_null',
  'lt',
  'lte',
  'gt',
  'gte',
  // Обидві межі включно: `[від, до]` значеннями типу поля.
  'between',
  // День (дата `YYYY-MM-DD` або токен дня) і діапазон днів (токен) у поясі годинника (FLT-13).
  'on',
  'before',
  'after',
  'within',
  'starts_with',
  // Мультисписок (multipicklist.md, розд. 3): «будь-яке з» (`&&`), «усі з» (`@>`), «жодного з» —
  // включає порожні, тож у правилах видимості його немає.
  'has_any',
  'has_all',
  'has_none',
  // Гроші (money-type.md, розд. 3): валюта суми з переліку кодів ISO 4217.
  'currency_in',
] as const;
export type FilterOperatorKey = (typeof FILTER_OPERATORS)[number];

/**
 * Оператори, що включають порожні значення (розд. 2): лише з ними `excludeEmpty: true` має сенс.
 * На іншому операторі `excludeEmpty: true` — `filter_operator_not_allowed`, `false` — без наслідків.
 */
export const EMPTY_INCLUSIVE_OPERATORS: ReadonlySet<string> = new Set([
  'neq',
  'not_in',
  'has_none',
]);

/** Оператори без значення: умова — лише про порожнє (FLT-15). */
export const NO_VALUE_OPERATORS: ReadonlySet<string> = new Set(['is_null', 'not_null']);

/**
 * Токени людини (розд. 3; FLT-14). Значення поля-користувача, яке сервер бере з контексту
 * доступу, а не з запиту:
 * - `me` — я;
 * - `my_teams` — я й учасники моїх команд;
 * - `my_unit` / `my_unit_tree` — лише на `owner`: записи мого підрозділу (з нащадками), як
 *   області `unit` / `unit_tree` разом із перехідним станом переведень (Д1, розд. 7).
 * У нелюдського принципала (сервіс, система, правило автоматизації) людини немає: у фільтрі —
 * `filter_token_unavailable`, у збереженому правилі видимості — `false`.
 */
export const USER_TOKENS = ['me', 'my_teams', 'my_unit', 'my_unit_tree'] as const;
export type UserToken = (typeof USER_TOKENS)[number];

/**
 * «Неактивні користувачі» (FLT-14): деактивовані й архівні люди IAM (`status in ('disabled',
 * 'archived')`, архівні — колишні працівники з перенесення). Від глядача не
 * залежить, тож діє й у нелюдського принципала; лише профіль `filter`, на полі-користувачі.
 */
export const INACTIVE_USERS_TOKEN = 'inactive_users';

/** Токени підрозділу: лише на системному полі `owner` (компілюються в `owner_unit_id`). */
export const UNIT_TOKENS: ReadonlySet<string> = new Set(['my_unit', 'my_unit_tree']);

/** Системне поле відповідального: поле-користувач, як і поля типу `user` (FLD-10). */
export const OWNER_FIELD = 'owner';
/** Системне поле підрозділу відповідального (оцінювач токенів підрозділу бере його з запису). */
export const OWNER_UNIT_FIELD = 'owner_unit';

/**
 * Токен `record` (контракт Б4; field-rules.md, розд. 2; filter-language.md, розд. 5): значення поля
 * запису, що пишеться чи редагується, або його батька на один крок (`<lookup>.<поле>`). Один на всі
 * місця: у профілі `requirement` він порівнює поле з полем того самого запису, у фільтрі вибору
 * зв'язку (профіль `filter` над ціллю) бере значення з форми. Форма — `{ token: 'record', field }`.
 */
export const RECORD_TOKEN = 'record';

export interface FilterToken {
  readonly token: string;
  /** Параметр токена дат (`days_ago`, `last_n_days`): у токенів людини його немає. */
  readonly n?: number;
  /** Лише токен `record`: ключ поля запису (`c_opened_on`) або крок (`c_deal.c_contact`). */
  readonly field?: string;
}
export type FilterScalar = string | number | boolean;
/**
 * Значення-гроші (розд. 2; money-type.md, розд. 3): сума рядком і код валюти. Порівнює суму в межах
 * валюти; число замість нього порівнює суму в усіх валютах. Не токен: ключів `token` немає.
 */
export interface FilterMoney {
  readonly amount: string;
  readonly currency: string;
}
export type FilterItem = FilterScalar | FilterToken | FilterMoney;
export type FilterValue = FilterItem | readonly FilterItem[];

export interface FilterCondition {
  /**
   * Канонічний ключ поля (ADR-0006): `c_amount`, `u_note`, системні `owner`, `updated_at`. У
   * профілі `requirement` — ще й крок через зв'язок `<lookup>.<поле батька>` (FLT-16, один крок).
   */
  readonly field: string;
  readonly op: string;
  readonly value?: FilterValue;
  /** «Без порожніх» для операторів, що включають порожні (`EMPTY_INCLUSIVE_OPERATORS`, FLT-15). */
  readonly excludeEmpty?: boolean;
}
export interface FilterAnd {
  readonly and: readonly FilterNode[];
}
export interface FilterOr {
  readonly or: readonly FilterNode[];
}
/**
 * Наявність живих дочірніх за вхідним зв'язком (FLT-16; field-rules.md, розд. 2): ключ — дочірня
 * сутність і її поле `lookup` на цей запис (`c_chat.c_person`). Лише профіль `requirement`, лише
 * `exists`; `where` і `min` зарезервовано (Next).
 */
export interface FilterRelated {
  readonly related: string;
  readonly exists: boolean;
}
export type FilterNode = FilterCondition | FilterAnd | FilterOr | FilterRelated;

/**
 * Межі профілю `filter` (розд. 4): умов усього, глибина груп (корінь — 0, умова в групі кореня —
 * 1), значень у масиві умови, довжина текстового значення, розмір дерева в байтах UTF-8.
 */
export const FILTER_LIMITS = {
  conditions: 20,
  depth: 2,
  values: 100,
  text: 200,
  bytes: 8 * 1024,
} as const;
export type FilterLimits = { readonly [K in keyof typeof FILTER_LIMITS]: number };

/**
 * Межі профілю `requirement` (field-rules.md, розд. 2): до 10 умов (вузол `related` теж умова),
 * глибина 2; решта — як у `filter`. Кроків через зв'язок і вузлів `related` — окремі межі нижче.
 */
export const REQUIREMENT_LIMITS: FilterLimits = { ...FILTER_LIMITS, conditions: 10 };
/** Різних кроків (полів `lookup`, через які читає умова, разом із токенами `record`) у дереві. */
export const REQUIREMENT_STEP_LIMIT = 3;
/** Вузлів `related … exists` у дереві. */
export const REQUIREMENT_RELATED_LIMIT = 2;

export type FilterLimitName = keyof typeof FILTER_LIMITS | 'steps' | 'related';

/** Профіль розбору (розд. 5). `rule` розбирає схема документа ролі (`@clientline/contracts/access`). */
export type FilterProfileName = 'filter' | 'requirement';

export interface ParseFilterOptions {
  /** `filter` за замовчуванням; `requirement` — крок, `related … exists` і токен `record`. */
  profile?: FilterProfileName;
  /**
   * Профіль `filter` з токеном `record` (фільтр вибору зв'язку, field-rules.md, розд. 5): значення
   * дає форма запису, що пишеться. Кроків і `related` фільтр вибору не отримує.
   */
  recordToken?: boolean;
  /**
   * Профіль `filter` з кроком через зв'язок `<lookup>.<поле батька>` (один крок, до
   * `REQUIREMENT_STEP_LIMIT` різних полів `lookup`): `when` правила за розкладом (A-C;
   * events-automations.md, розд. 7.3). Чи досяжний зв'язок, вирішує викликач (`via` правила).
   */
  steps?: boolean;
}

/** Коди помилок мови умов (розд. 9). `request_malformed` — 400, решта — 422. */
export type FilterProblemCode =
  | 'request_malformed'
  | 'filter_field_unavailable'
  | 'filter_field_not_filterable'
  | 'filter_operator_not_allowed'
  | 'filter_value_invalid'
  | 'filter_token_unavailable'
  | 'filter_too_complex'
  | 'filter_feature_unavailable';

export interface FilterProblem {
  code: FilterProblemCode;
  /**
   * `{ field, op }` — `filter_operator_not_allowed`, `{ field }` — `filter_value_invalid`,
   * `{ limit }` — `filter_too_complex`. Ключ поля — лише видимого поля (розд. 6, п. 1).
   */
  params?: { field?: string; op?: string; limit?: FilterLimitName };
}

export type ParsedFilter =
  | {
      ok: true;
      tree: FilterNode;
      conditions: readonly FilterCondition[];
      /** Вузли `related … exists` у порядку дерева (лише профіль `requirement`). */
      related: readonly FilterRelated[];
    }
  | { ok: false; problem: FilterProblem };

const KEY = /^[a-z][a-z0-9_]{0,62}$/;
/** Крок через зв'язок (`c_company.c_city`, FLT-16): рівно один крок. */
const STEP = /^[a-z][a-z0-9_]{0,62}\.[a-z][a-z0-9_]{0,62}$/;
/** Кілька кроків (`a.b.c`) — зарезервовано, Next. */
const PATH = /^[a-z][a-z0-9_]{0,62}(\.[a-z][a-z0-9_]{0,62})+$/;
const TOKEN_NAME = /^[a-z][a-z0-9_]{0,39}$/;
const CONDITION_KEYS: ReadonlySet<string> = new Set(['field', 'op', 'value', 'excludeEmpty']);
/** Ключі вузла `related`: `where` і `min` — зарезервовані (Next), решта — граматична помилка. */
const RELATED_KEYS: ReadonlySet<string> = new Set(['related', 'exists']);
const RELATED_RESERVED: ReadonlySet<string> = new Set(['where', 'min']);
const KNOWN_OPERATORS: ReadonlySet<string> = new Set(CONTRACT_OPERATORS);
const HUMAN_TOKENS: ReadonlySet<string> = new Set(USER_TOKENS);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Токен-об'єкт мови умов (розд. 3): `token` і необов'язкове ціле `n`; токен `record` — лише з
 * `field` (ключ поля або один крок), без `n`. `field` в іншого токена й `record` без поля — не токени.
 */
export function isFilterToken(value: unknown): value is FilterToken {
  if (!isRecord(value)) return false;
  const keys = Object.keys(value);
  if (!keys.every((key) => key === 'token' || key === 'n' || key === 'field')) return false;
  if (typeof value.token !== 'string' || !TOKEN_NAME.test(value.token)) return false;
  if (value.token === RECORD_TOKEN) {
    return (
      value.n === undefined &&
      typeof value.field === 'string' &&
      (KEY.test(value.field) || STEP.test(value.field))
    );
  }
  if (value.field !== undefined) return false;
  return value.n === undefined || Number.isSafeInteger(value.n);
}

/** Токен `record` (Б4): значення поля запису, що пишеться. */
export function isRecordToken(value: unknown): value is FilterToken & { field: string } {
  return isFilterToken(value) && value.token === RECORD_TOKEN;
}

/** Крок через зв'язок `<lookup>.<поле батька>` або `null`, якщо ключ — власне поле. */
export function stepOf(path: string): { lookup: string; field: string } | null {
  if (!STEP.test(path)) return null;
  const dot = path.indexOf('.');
  return { lookup: path.slice(0, dot), field: path.slice(dot + 1) };
}

/** Вузол `related … exists` (FLT-16): для обходу дерева без `in`-перевірок. */
export function isFilterRelated(node: FilterNode): node is FilterRelated {
  return 'related' in node;
}

/** Ключ `related`: дочірня сутність і її поле `lookup` на цей запис. */
export function relatedOf(key: string): { entity: string; lookup: string } | null {
  const step = stepOf(key);
  return step ? { entity: step.lookup, lookup: step.field } : null;
}

/** Значення-гроші за граматикою: рівно `amount` і `currency`, обидва рядки (зміст — за типом поля). */
export function isFilterMoney(value: unknown): value is FilterMoney {
  if (!isRecord(value)) return false;
  const keys = Object.keys(value);
  return (
    keys.length === 2 && typeof value.amount === 'string' && typeof value.currency === 'string'
  );
}

function isScalar(value: unknown): value is FilterScalar {
  return (
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    (typeof value === 'number' && Number.isFinite(value))
  );
}

/** Форма значення за граматикою (розд. 1, 3): скаляр, токен, гроші або масив із них. */
function valueShape(value: unknown): 'ok' | 'malformed' {
  const item = (v: unknown) => isScalar(v) || isFilterToken(v) || isFilterMoney(v);
  if (item(value)) return 'ok';
  if (Array.isArray(value)) return value.every(item) ? 'ok' : 'malformed';
  return 'malformed';
}

/** Ключі групи — рівно `and` або `or` з непорожнім масивом. */
function groupItems(node: Record<string, unknown>): readonly unknown[] | null {
  const keys = Object.keys(node);
  if (keys.length !== 1) return null;
  const items = keys[0] === 'and' ? node.and : keys[0] === 'or' ? node.or : undefined;
  return Array.isArray(items) && items.length > 0 ? items : null;
}

const malformed: ParsedFilter = { ok: false, problem: { code: 'request_malformed' } };
const tooComplex = (limit: FilterLimitName): ParsedFilter => ({
  ok: false,
  problem: { code: 'filter_too_complex', params: { limit } },
});
const unavailable: ParsedFilter = { ok: false, problem: { code: 'filter_feature_unavailable' } };
const tokenUnavailable: ParsedFilter = {
  ok: false,
  problem: { code: 'filter_token_unavailable' },
};

/**
 * Розбір вузла `related` профілю `requirement` (FLT-16): `{ related: '<сутність>.<lookup>',
 * exists: true | false }`. `where` і `min` — зарезервовані (`filter_feature_unavailable`), інші
 * ключі й форма — граматична помилка.
 */
function relatedNode(node: Record<string, unknown>): FilterRelated | ParsedFilter {
  const keys = Object.keys(node);
  if (!keys.every((key) => RELATED_KEYS.has(key) || RELATED_RESERVED.has(key))) return malformed;
  if (keys.some((key) => RELATED_RESERVED.has(key))) return unavailable;
  if (typeof node.related !== 'string' || typeof node.exists !== 'boolean') return malformed;
  if (!STEP.test(node.related)) return PATH.test(node.related) ? unavailable : malformed;
  return node as unknown as FilterRelated;
}

/**
 * Розбір дерева за граматикою і межами профілю (розд. 1, 4, 5): без реєстру полів і прав, тож
 * однаковий у браузері й на сервері. Обхід — без рекурсії, з ранньою зупинкою на межах: глибоке чи
 * широке дерево не обходиться далі за межу. Порядок умов у результаті — порядок дерева (у ньому ж
 * DAL перевіряє поля). Межі не залежать від поля, тож відповідь не розкриває, чи поле приховане
 * (розд. 6, п. 1).
 *
 * Профіль `filter`: крок через зв'язок (крім `steps`) і `related` — `filter_feature_unavailable`,
 * токен `record` — `filter_token_unavailable`, крім фільтра вибору (`recordToken`). Профіль
 * `requirement` (field-rules.md, розд. 2): один крок (`<lookup>.<поле>`, кілька — Next), `related
 * … exists` без `where` і `min`, токен `record`; токенів людини немає (`filter_token_unavailable`):
 * умова над записом залежить від даних, а не від того, хто пише. Межі — `REQUIREMENT_LIMITS`, до
 * `REQUIREMENT_STEP_LIMIT` різних кроків і `REQUIREMENT_RELATED_LIMIT` вузлів `related`.
 */
export function parseFilterTree(
  input: unknown,
  limits?: FilterLimits,
  options: ParseFilterOptions = {},
): ParsedFilter {
  const requirement = options.profile === 'requirement';
  const bounds = limits ?? (requirement ? REQUIREMENT_LIMITS : FILTER_LIMITS);
  const recordAllowed = requirement || options.recordToken === true;
  const stepAllowed = requirement || options.steps === true;
  const conditions: FilterCondition[] = [];
  const related: FilterRelated[] = [];
  /** Поля `lookup`, через які умова читає батька: у полі умови й у токені `record`. */
  const steps = new Set<string>();
  const stack: { node: unknown; depth: number }[] = [{ node: input, depth: 0 }];
  while (stack.length > 0) {
    const { node, depth } = stack.pop() as { node: unknown; depth: number };
    if (!isRecord(node)) return malformed;
    if ('related' in node) {
      if (!requirement) return unavailable;
      const parsed = relatedNode(node);
      if ('ok' in parsed) return parsed;
      related.push(parsed);
      if (related.length > REQUIREMENT_RELATED_LIMIT) return tooComplex('related');
      if (conditions.length + related.length > bounds.conditions) return tooComplex('conditions');
      continue;
    }
    if ('and' in node || 'or' in node) {
      const items = groupItems(node);
      if (!items) return malformed;
      if (depth + 1 > bounds.depth) return tooComplex('depth');
      // Діти — у зворотному порядку: стек віддає їх у порядку дерева.
      for (let i = items.length - 1; i >= 0; i--) stack.push({ node: items[i], depth: depth + 1 });
      continue;
    }
    if (!Object.keys(node).every((key) => CONDITION_KEYS.has(key))) return malformed;
    const { field, op } = node;
    if (typeof field !== 'string' || typeof op !== 'string') return malformed;
    if (!KEY.test(field)) {
      if (!stepAllowed || !STEP.test(field)) return PATH.test(field) ? unavailable : malformed;
      steps.add(stepOf(field)?.lookup as string);
    }
    if (!KNOWN_OPERATORS.has(op)) return malformed;
    if ('value' in node && valueShape(node.value) === 'malformed') return malformed;
    if ('excludeEmpty' in node && typeof node.excludeEmpty !== 'boolean') return malformed;
    if (Array.isArray(node.value) && node.value.length > bounds.values) return tooComplex('values');
    for (const item of valuesOf(node as unknown as FilterCondition)) {
      if (!isFilterToken(item)) continue;
      if (item.token === RECORD_TOKEN) {
        if (!recordAllowed) return tokenUnavailable;
        // Фільтр вибору бере з токена лише значення форми чи батька форми (field-rules.md, розд. 5).
        const step = stepOf(item.field as string);
        if (step) steps.add(step.lookup);
      } else if (requirement && HUMAN_TOKENS.has(item.token)) {
        return tokenUnavailable;
      }
    }
    if (stepAllowed && steps.size > REQUIREMENT_STEP_LIMIT) return tooComplex('steps');
    conditions.push(node as unknown as FilterCondition);
    if (conditions.length + related.length > bounds.conditions) return tooComplex('conditions');
  }
  if (new TextEncoder().encode(JSON.stringify(input)).length > bounds.bytes) {
    return tooComplex('bytes');
  }
  return { ok: true, tree: input as FilterNode, conditions, related };
}

const IMPLEMENTED_OPERATORS: ReadonlySet<string> = new Set(FILTER_OPERATORS);

/**
 * Умова як дані метаданих (подання й зв'язки пакета компанії, умови поведінок): граматика й межі
 * профілю плюс помилки, які мова умов інакше дала б лише під час виконання (розд. 5): оператор
 * контракту, якого DAL ще не реалізує (`filter_operator_not_allowed`), і текст понад межу
 * (`filter_value_invalid`). Повертає код розд. 9 (з назвою межі для `filter_too_complex`) або
 * `null`; значень умов у відповіді немає. Оператор за типом поля, індекс і поля за реєстром
 * перевіряє мова умов там, де умову застосовують.
 */
export function filterConfigProblem(
  input: unknown,
  options: ParseFilterOptions = {},
): string | null {
  const parsed = parseFilterTree(input, undefined, options);
  if (!parsed.ok) {
    const limit = parsed.problem.params?.limit;
    return limit ? `${parsed.problem.code} (${limit})` : parsed.problem.code;
  }
  for (const condition of parsed.conditions) {
    if (!IMPLEMENTED_OPERATORS.has(condition.op)) return 'filter_operator_not_allowed';
    const tooLong = valuesOf(condition).some(
      (item) => typeof item === 'string' && item.length > FILTER_LIMITS.text,
    );
    if (tooLong) return 'filter_value_invalid';
  }
  return null;
}

/** Умова чи група: для обходу дерева без `in`-перевірок на кожному кроці. */
export function isFilterGroup(node: FilterNode): node is FilterAnd | FilterOr {
  return 'and' in node || 'or' in node;
}

/**
 * Що читає умова (профіль `requirement`): власні поля запису (`own`, зокрема поле `lookup` кроку й
 * поле токена `record`), кроки через зв'язок (`steps`, `<lookup>.<поле>`) і ключі `related`. Для
 * перевірки «умова не читає прихованого» (PIP-10, без оракула значень) і охоронця overlay.
 * Порядок — порядок дерева, без повторів.
 */
export function conditionPaths(node: FilterNode): {
  own: string[];
  steps: string[];
  related: string[];
} {
  const own = new Set<string>();
  const steps = new Set<string>();
  const related = new Set<string>();
  const visitField = (path: string) => {
    const step = stepOf(path);
    if (step) {
      own.add(step.lookup);
      steps.add(path);
    } else {
      own.add(path);
    }
  };
  const visit = (current: FilterNode) => {
    if (isFilterGroup(current)) {
      for (const item of groupOf(current).items) visit(item);
      return;
    }
    if (isFilterRelated(current)) {
      related.add(current.related);
      return;
    }
    visitField(current.field);
    for (const item of valuesOf(current)) {
      if (isRecordToken(item)) visitField(item.field);
    }
  };
  visit(node);
  return { own: [...own], steps: [...steps], related: [...related] };
}

/** Діти групи і її зв'язка. */
export function groupOf(node: FilterAnd | FilterOr): {
  kind: 'and' | 'or';
  items: readonly FilterNode[];
} {
  return 'and' in node ? { kind: 'and', items: node.and } : { kind: 'or', items: node.or };
}

/** Значення умови масивом (без значення — порожній; скаляр, токен і гроші — один елемент). */
export function valuesOf(condition: FilterCondition): readonly FilterItem[] {
  const { value } = condition;
  if (value === undefined) return [];
  return Array.isArray(value) ? value : [value as FilterItem];
}

/**
 * Базовий тип значення поля (як порівнювати й приводити в SQL, FLD-01): спільний для DAL
 * (`BASE_TYPE`) і оцінювача. Відображення типу поля на базовий — `FIELD_BASE_TYPE` (`base-type.ts`).
 */
export type BaseFieldType =
  | 'text'
  | 'picklist'
  | 'multipicklist'
  | 'number'
  | 'date'
  | 'datetime'
  | 'uuid'
  | 'boolean'
  /**
   * Автономер (FLD-22): порівнюється числом `<колонка>__n`; значення умови — число або рядок за
   * шаблоном поля (розбирає `autonumberValue`, autonumber-tags.md, розд. 1).
   */
  | 'autonumber'
  /**
   * Гроші (FLD-05; money-type.md, розд. 3): сума `numeric` і валюта `<колонка>__currency`. Значення
   * умови — гроші `{ amount, currency }` (у межах валюти) або число (сума в усіх валютах).
   */
  | 'money';

// ---------------------------------------------------------------- помічники DSL (розд. 1)

export const and = (...items: FilterNode[]): FilterAnd => ({ and: items });
export const or = (...items: FilterNode[]): FilterOr => ({ or: items });
export const eq = (field: string, value: FilterItem): FilterCondition => ({
  field,
  op: 'eq',
  value,
});
export const inList = (field: string, values: readonly FilterItem[]): FilterCondition => ({
  field,
  op: 'in',
  value: values,
});
export const isNull = (field: string): FilterCondition => ({ field, op: 'is_null' });
export const notNull = (field: string): FilterCondition => ({ field, op: 'not_null' });
export const me = (): FilterToken => ({ token: 'me' });
export const myTeams = (): FilterToken => ({ token: 'my_teams' });
export const myUnit = (): FilterToken => ({ token: 'my_unit' });
export const myUnitTree = (): FilterToken => ({ token: 'my_unit_tree' });
export const inactiveUsers = (): FilterToken => ({ token: INACTIVE_USERS_TOKEN });
export const neq = (field: string, value: FilterScalar): FilterCondition => ({
  field,
  op: 'neq',
  value,
});
export const notIn = (field: string, values: readonly FilterItem[]): FilterCondition => ({
  field,
  op: 'not_in',
  value: values,
});
export const between = (field: string, from: FilterScalar, to: FilterScalar): FilterCondition => ({
  field,
  op: 'between',
  value: [from, to],
});
export const onDay = (field: string, day: string | FilterToken): FilterCondition => ({
  field,
  op: 'on',
  value: day,
});
export const within = (field: string, range: FilterToken): FilterCondition => ({
  field,
  op: 'within',
  value: range,
});
/** Токен `record` (профіль `requirement`, фільтр вибору): поле запису, що пишеться, чи крок. */
export const recordValue = (field: string): FilterToken => ({ token: RECORD_TOKEN, field });
/** `related … exists` (профіль `requirement`): є (`true`) чи немає живих дочірніх. */
export const relatedExists = (key: string, exists = true): FilterRelated => ({
  related: key,
  exists,
});
export const today = (): FilterToken => ({ token: 'today' });
export const daysAgo = (n: number): FilterToken => ({ token: 'days_ago', n });
export const lastNDays = (n: number): FilterToken => ({ token: 'last_n_days', n });
export const nextNDays = (n: number): FilterToken => ({ token: 'next_n_days', n });
export const now = (): FilterToken => ({ token: 'now' });
